import { t } from "i18next";
import { SavedCommentDto } from "../../../../types/dtos/forms";
import { FormType, ValidationResult } from "../../../../types/forms";
import { Badge, Label, VoiceTextArea } from "../../../common";
import { FormConstants } from "../../../../config/forms/FormConstants";

interface BehaviourCommentProps {
  behaviourId: number;
  formId: number;
  questionIsRequired?: boolean | null;
  questionTextTranslationKey: string;
  formType: FormType;
  currentValue: string | undefined;
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  isReadOnly?: boolean;
  flaggedChangeComponent?: JSX.Element | null;
  /** The onChange event, for handling state changes */
  onChange(newValue: string, behaviourId: number, clientFormId: number): void;
  /** An event to call when the input loses focus */
  onBlur?(
    behaviourId: number,
    nonStateValue: SavedCommentDto | null
  ): void | undefined;
}

function BehaviourComment({
  behaviourId,
  formId,
  questionIsRequired,
  questionTextTranslationKey,
  formType,
  currentValue,
  showValidationErrors,
  isReadOnly = false,
  flaggedChangeComponent = null,
  onChange,
  onBlur = undefined,
}: BehaviourCommentProps) {
  if (!questionTextTranslationKey) return null;

  const textAreaElementId = `txtBhvComment${behaviourId}`;
  const isJourney = formType === "JOURNEY";
  const shellClassName = isJourney
    ? "mt-2 p-2 w-full rounded-md bg-white/20 border-0"
    : "mt-2 p-2 w-full rounded-md border border-gray-400";
  const inputClassName = isJourney
    ? "w-full placeholder:text-white/70 placeholder:text-sm focus:outline-none focus:ring-0"
    : "w-full focus:outline-none focus:ring-0";
  const actionLinkClassName = isJourney
    ? "text-sm text-white underline cursor-pointer hover:text-white/80"
    : undefined;
  const statusTextClassName = isJourney ? "text-xs text-white/80" : undefined;
  const idleMicClassName = isJourney
    ? "text-white/50 hover:text-white/70 focus:outline-none"
    : undefined;
  const errorAsBadge = isJourney;

  const handleTextChange = (newValue: string) => {
    onChange(newValue, behaviourId, formId);
  };

  const handleTextFieldBlur = () => {
    if (onBlur) {
      onBlur(behaviourId, null);
    }
  };

  const commentIsValid = () => {
    return (
      currentValue !== undefined &&
      currentValue !== null &&
      currentValue.trim().length > 0
    );
  };

  // Perform the validation, if we're showing validation errors
  let validationResult: ValidationResult | null = null;
  if (showValidationErrors && questionIsRequired == true && !commentIsValid()) {
    validationResult = new ValidationResult(false, [{ errorType: "REQUIRED" }]);
  }

  const placeholderText = questionIsRequired ? "Required" : "Optional";

  return (
    <div>
      <Label
        text={t(questionTextTranslationKey)}
        className={formType === "JOURNEY" ? "text-white" : "text-gray-700"}
        htmlFor={textAreaElementId}
      />
      {questionIsRequired == null ||
        (questionIsRequired == false && (
          <Badge
            text={t("Common.Optional")}
            marginClassName="mx-1"
            backgroundColourClassName="bg-gray-200/75"
            textColourClassName="text-gray-400"
          />
        ))}
      {flaggedChangeComponent}
      <VoiceTextArea
        inputId={textAreaElementId}
        shellClassName={shellClassName}
        inputClassName={inputClassName}
        actionLinkClassName={actionLinkClassName}
        statusTextClassName={statusTextClassName}
        idleMicClassName={idleMicClassName}
        errorAsBadge={errorAsBadge}
        errorBadgeBackgroundColourClassName="bg-white"
        errorBadgeTextColourClassName="text-red-700"
        errorBadgeBorderRadiusClassName="rounded-full"
        minRows={2}
        onChange={handleTextChange}
        onBlur={handleTextFieldBlur}
        value={currentValue}
        isReadOnly={isReadOnly}
        showValidationErrors={showValidationErrors}
        validationResult={validationResult}
        maxLength={FormConstants.MaxLengths.AnswerSetComment}
        placeholder={placeholderText}
      />
    </div>
  );
}

export default BehaviourComment;
